'use client';

import { mrDistance, mrLerpPosition } from './interpolation';

export type PredictionInput = {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  startedAtMs: number;
  etaMs: number;
  nowMs: number;
};

export type PathPredictionInput = PredictionInput & {
  path?: Array<[number, number]>;
};

export function mrPredictPosition(input: PredictionInput): { x: number; y: number } {
  if (input.etaMs <= input.startedAtMs) {
    return { x: input.targetX, y: input.targetY };
  }
  const alpha = (input.nowMs - input.startedAtMs) / (input.etaMs - input.startedAtMs);
  return mrLerpPosition({ x: input.x, y: input.y }, { x: input.targetX, y: input.targetY }, alpha);
}

export function mrPredictPathPosition(input: PathPredictionInput): { x: number; y: number } {
  if (!input.path || input.path.length < 2 || input.etaMs <= input.startedAtMs) {
    return mrPredictPosition(input);
  }

  const alpha = Math.min(1, Math.max(0, (input.nowMs - input.startedAtMs) / (input.etaMs - input.startedAtMs)));
  const distance = pathLength(input.path) * alpha;
  return pointAtDistance(input.path, distance);
}

function pathLength(path: Array<[number, number]>): number {
  let total = 0;
  for (let index = 1; index < path.length; index += 1) {
    const from = path[index - 1];
    const to = path[index];
    total += mrDistance({ x: from[0], y: from[1] }, { x: to[0], y: to[1] });
  }
  return total;
}

function pointAtDistance(path: Array<[number, number]>, distance: number): { x: number; y: number } {
  if (path.length === 0) {
    return { x: 0, y: 0 };
  }
  if (path.length === 1 || distance <= 0) {
    return { x: path[0][0], y: path[0][1] };
  }

  let remaining = distance;
  for (let index = 1; index < path.length; index += 1) {
    const from = path[index - 1];
    const to = path[index];
    const segmentLength = mrDistance({ x: from[0], y: from[1] }, { x: to[0], y: to[1] });
    if (segmentLength <= 0.0001) {
      continue;
    }
    if (remaining <= segmentLength) {
      return mrLerpPosition(
        { x: from[0], y: from[1] },
        { x: to[0], y: to[1] },
        remaining / segmentLength,
      );
    }
    remaining -= segmentLength;
  }

  const last = path[path.length - 1];
  return { x: last[0], y: last[1] };
}
